import React, { useMemo, useEffect, useState, useRef } from "react";
import { apiGet } from "../api";
import type { Locale } from "../i18n";
import { t } from "../i18n";
import { SvgIcon } from "../components/SvgIcon";

export function VerifyRecoveryEmailView(props: { locale: Locale }) {
  const token = useMemo(() => {
    const u = new URL(window.location.href);
    return u.searchParams.get("token") ?? "";
  }, []);

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasVerified = useRef(false);

  const isInvalidToken = !token;

  useEffect(() => {
    if (isInvalidToken) {
      setLoading(false);
      return;
    }

    if (hasVerified.current) return;
    hasVerified.current = true;

    (async () => {
      try {
        const res = await apiGet<{ ok: true; email: string }>(`/v1/account/recovery/email/verify?token=${encodeURIComponent(token)}`);

        if (!res.ok) {
          setError(res.error || "Verification failed");
          setLoading(false);
          return;
        }

        setLoading(false);
        setSuccess(true);
      } catch (err) {
        setLoading(false);
        setError("An error occurred during verification");
      }
    })();
  }, [token, isInvalidToken]);

  return (
    <div className="authShell">
      <div className="authBg" />
      <div className="authWrap">
        <a className="authBrand" href="/" aria-label="KVEJA home">
          <img className="authLogo" src="/brand/kveja_logo.png" alt="KVEJA" />
        </a>

        <div className="authCard">
          <div className="authHeader">
            <h1 className="authTitle">Verify recovery email</h1>
            <p className="authSubtitle">Complete your recovery email setup</p>
          </div>

          {isInvalidToken ? (
            <div className="authForm">
              <div className="authError">Invalid verification link</div>
              <div className="authRow authRowBottom">
                <a className="btn authBtnSecondary" href="/login">Back to login</a>
              </div>
            </div>
          ) : loading ? (
            <div className="authForm">
              <div style={{ textAlign: "center", padding: "32px 0", color: "var(--muted)" }}>
                <div style={{ fontSize: "14px", marginBottom: "12px" }}>Verifying email…</div>
              </div>
            </div>
          ) : error ? (
            <div className="authForm">
              <div className="authError">{error}</div>
              <div style={{ fontSize: "12px", color: "var(--muted)", marginTop: "12px", lineHeight: "1.5" }}>
                The verification link may have expired. You can request a new one by adding the recovery email again in your settings.
              </div>
              <div className="authRow authRowBottom">
                <a className="btn authBtnSecondary" href="/login">Back to login</a>
              </div>
            </div>
          ) : (
            <div className="authForm">
              <div style={{ textAlign: "center", marginBottom: "24px" }}>
                <div style={{ fontSize: "32px", marginBottom: "12px", color: "var(--success, #10b981)" }}>
                  <SvgIcon name="check-circle-fill" title="Verified" />
                </div>
                <p className="authSuccess">Recovery email verified successfully!</p>
              </div>
              <div style={{ fontSize: "13px", color: "var(--muted)", marginBottom: "20px", lineHeight: "1.5" }}>
                Your recovery email has been added to your account. You can now use it to recover access if needed.
              </div>
              <div className="authRow authRowBottom">
                <a className="btn primary authBtn" href="/login">Sign in</a>
              </div>
            </div>
          )}
        </div>

        <div className="authFooter">
          <span className="small">KVEJA</span>
          <span className="authDot" aria-hidden="true">·</span>
          <a className="small" href="/">Back to landing</a>
        </div>
      </div>
    </div>
  );
}
